import { useSyncExternalStore } from 'react';
import { EventBus, EVENTS } from '../../shared/EventBus';
import type { TimedRunStateMessage } from '../../shared/types/TimedRun';

export interface TimedRunClientSnapshot {
  message: TimedRunStateMessage | null;
  /** Approximate server Date.now() minus client Date.now() at receipt. */
  serverOffsetMs: number;
}

const EMPTY_SNAPSHOT: TimedRunClientSnapshot = {
  message: null,
  serverOffsetMs: 0,
};

let snapshot = EMPTY_SNAPSHOT;
let unbindEventBus: (() => void) | null = null;
const listeners = new Set<() => void>();

function publish(message: TimedRunStateMessage): void {
  snapshot = {
    message,
    serverOffsetMs: message.serverNow - Date.now(),
  };
  listeners.forEach(listener => listener());
}

function ensureEventBusBinding(): void {
  if (unbindEventBus) return;
  unbindEventBus = EventBus.getInstance().on(
    EVENTS.NET_MESSAGE_TIMED_RUN_STATE,
    publish,
  );
}

function subscribe(listener: () => void): () => void {
  ensureEventBusBinding();
  listeners.add(listener);
  return () => listeners.delete(listener);
}

export function getTimedRunClientSnapshot(): TimedRunClientSnapshot {
  ensureEventBusBinding();
  return snapshot;
}

export function useTimedRunClientState(): TimedRunClientSnapshot {
  return useSyncExternalStore(subscribe, getTimedRunClientSnapshot, () => EMPTY_SNAPSHOT);
}

export function timedRunElapsedMs(
  state: TimedRunClientSnapshot,
  clientNow: number = Date.now(),
): number {
  const run = state.message?.run;
  if (!run) return 0;
  if (run.status !== 'running') {
    return Math.max(0, run.elapsedMs ?? ((run.finishedAt ?? run.startedAt) - run.startedAt));
  }
  return Math.max(0, clientNow + state.serverOffsetMs - run.startedAt);
}

export function formatTimedRunElapsed(ms: number): string {
  const bounded = Math.max(0, Math.floor(ms));
  const hours = Math.floor(bounded / 3_600_000);
  const minutes = Math.floor((bounded % 3_600_000) / 60_000);
  const seconds = Math.floor((bounded % 60_000) / 1_000);
  const centiseconds = Math.floor((bounded % 1_000) / 10);
  const mm = (hours > 0 ? minutes.toString().padStart(2, '0') : minutes.toString());
  const base = `${mm}:${seconds.toString().padStart(2, '0')}.${centiseconds.toString().padStart(2, '0')}`;
  return hours > 0 ? `${hours}:${base}` : base;
}

/** Test-only reset; production state is replaced by each owner projection. */
export function resetTimedRunClientStateForTests(): void {
  unbindEventBus?.();
  unbindEventBus = null;
  snapshot = EMPTY_SNAPSHOT;
  listeners.clear();
}
